

import { AnimatePresence, motion } from "framer-motion";
import { useState, useRef, useLayoutEffect, useId } from "react";

type IconTooltipProps = {
  icon: React.ElementType;
  tooltip: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
};

export const IconTooltip: React.FC<IconTooltipProps> = ({
  icon: Icon,
  tooltip,
  onClick,
  disabled = false,
  className = "",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipId = useId();

  // Layout effect (pre-paint) so the measured clamp never flashes: center on
  // the trigger, then keep the bubble inside the viewport — icons commonly sit
  // at the far right (e.g. the notifications header) where a naive center
  // anchor pushes the tooltip off-screen on phones.
  useLayoutEffect(() => {
    if (!showTooltip || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const margin = 8;
    const half = (tipRef.current?.offsetWidth ?? 0) / 2;
    const center = rect.left + rect.width / 2;
    const left = Math.max(
      margin + half,
      Math.min(center, window.innerWidth - margin - half),
    );
    setPosition({ top: rect.top, left });
  }, [showTooltip, tooltip]);

  return (
    <div className="inline-flex">
      <button
        ref={buttonRef}
        type="button"
        className={`${disabled ? "opacity-50" : ""} ${className}`}
        // Icon-only: the tooltip text is the button's accessible name, and the
        // open bubble is its description (screen readers heard a nameless
        // "button" and never the bubble's text).
        aria-label={tooltip}
        aria-describedby={showTooltip ? tipId : undefined}
        // Touch fallback: consumers without their own onClick get tap-to-open.
        // Hover/focus handlers are gated below so the synthetic mouseenter and
        // tap-focus that PRECEDE click on touch don't pre-open the tooltip and
        // turn this into a self-cancelling toggle (open must survive the full
        // touch event sequence: pointerenter -> focus -> click).
        onClick={
          disabled ? undefined : (onClick ?? (() => setShowTooltip(true)))
        }
        disabled={disabled}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setShowTooltip(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setShowTooltip(false);
        }}
        onFocus={(e) => {
          // Keyboard focus only — a tap also focuses, and on consumers with
          // their own onClick that would pin the tooltip open on touch.
          if (!disabled && e.target.matches(":focus-visible")) {
            setShowTooltip(true);
          }
        }}
        onBlur={() => setShowTooltip(false)}
      >
        <Icon size={20} className="font-secondary" />
      </button>

      {/* Fullscreen overlay tooltip */}
      {
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="fixed inset-0 z-50 pointer-events-none"
            >
              <div
                ref={tipRef}
                id={tipId}
                role="tooltip"
                style={{
                  top: `${position.top - 40}px`, // Position above the button
                  left: `${position.left}px`,
                  transform: "translateX(-50%)",
                }}
                className="absolute z-50 surface-primary w-max max-w-[calc(100vw-1rem)] shadow-lg rounded-lg px-3 py-2 border mt-1 pointer-events-auto outline-quaternary"
              >
                <p className="body-3-regular font-secondary">{tooltip}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      }
    </div>
  );
};
